import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowsClockwise, Sparkle, Warning, CheckCircle, X } from "@phosphor-icons/react";

/** Shared assignment / reassignment modal (Phase 6C).
 *  Used by Hotlist inline actions and the /assignments page.
 *  Backend enforces role, capacity and status — this UI mirrors it.
 */
const REASONS = ["workload", "leave", "performance", "resignation", "business", "other"];

export default function AssignmentModal({ candidate, onClose, onDone }) {
  const [mode, setMode] = useState("recommended");
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState("business");
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allEmps, setAllEmps] = useState([]);

  const loadRecs = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/candidates/${candidate.id}/recommendations?top=5`);
      setRecos(r.data.recommendations || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load recommendations"); }
    finally { setLoading(false); }
  };
  const loadAll = async () => {
    try {
      const r = await api.get("/directory/employees?page_size=200");
      const rows = (r.data.rows || []).filter((u) =>
        ["sde", "sdl", "executive", "senior_executive"].includes(u.role) && !u.deleted_at
      );
      setAllEmps(rows);
    } catch { /* silent */ }
  };
  useEffect(() => { loadRecs(); loadAll(); }, [candidate.id]);

  const isReassign = !!candidate.assigned_employee_id;

  const submit = async () => {
    setBusy(true);
    try {
      if (isReassign) {
        // reassign endpoint requires reason
        await api.post(`/candidates/${candidate.id}/reassign`, {
          employee_id: employeeId, reason: reason || "reassigned", reason_code: reasonCode,
          override_capacity: override,
        });
      } else {
        await api.post(`/candidates/${candidate.id}/assign`, {
          mode, employee_id: mode === "automatic" ? null : employeeId,
          reason: reason || null, reason_code: reasonCode, override_capacity: override,
        });
      }
      toast.success(isReassign ? "Reassigned" : "Assigned");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50" role="dialog">
      <div className="bg-white rounded-lg p-6 w-[720px] max-h-[90vh] overflow-y-auto space-y-4"
           data-testid="assign-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{isReassign ? "Reassign" : "Assign"} — {candidate.full_name}</h3>
            <p className="text-xs text-slate-500">Current owner: {candidate.assigned_employee_name || "Unassigned"}</p>
          </div>
          <button onClick={onClose}><X size={16} /></button>
        </div>

        <div className="flex gap-2">
          {["manual", "automatic", "recommended"].map((m) => (
            <button key={m} onClick={() => setMode(m)}
                    data-testid={`mode-${m}`}
                    className={`px-3 py-1.5 text-sm rounded-md border ${mode === m ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>
              {m === "manual" ? "Manual" : m === "automatic" ? "Automatic" : "Recommended"}
            </button>
          ))}
        </div>

        {mode === "recommended" && (
          <div className="space-y-2" data-testid="recommendations-panel">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Top 5 Recommendations</div>
              <button onClick={loadRecs} className="text-xs text-slate-500 inline-flex items-center gap-1">
                <ArrowsClockwise size={12} /> Refresh
              </button>
            </div>
            {loading ? <div className="text-sm text-slate-400 p-2">Loading recommendations…</div> : recos.length === 0 ? (
              <div className="text-sm text-amber-700 p-2 bg-amber-50 rounded-md border border-amber-200 inline-flex items-center gap-1">
                <Warning size={14} /> No eligible employee with available capacity.
              </div>
            ) : (
              <div className="space-y-2">
                {recos.map((r) => (
                  <label key={r.employee_id} className={`block border rounded-md px-3 py-2 cursor-pointer ${employeeId === r.employee_id ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}
                         data-testid={`rec-row-${r.employee_id}`}>
                    <input type="radio" name="rec" className="mr-2" checked={employeeId === r.employee_id}
                           onChange={() => setEmployeeId(r.employee_id)} />
                    <span className="font-medium">{r.employee_name}</span>
                    <span className="text-xs text-slate-400 ml-2">{r.employee_code} · {r.team || "—"}</span>
                    <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
                      <span>Load: {r.current_load}/{r.max_capacity}</span>
                      <span>Available: <b>{r.available_capacity}</b></span>
                      <span>Success: {r.success_pct}%</span>
                      <span>Offers won: {r.offers_won}</span>
                      <span className="text-emerald-700">Score: <b>{r.score}</b></span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "manual" && (
          <div>
            <label className="text-xs text-slate-500">Select Employee</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                    data-testid="manual-employee-select"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              <option value="">— select —</option>
              {allEmps.map((e) => (
                <option key={e.id} value={e.id}>
                  {`${e.name} · ${e.employee_code || ""} · ${e.team || "—"} · ${e.available_capacity ?? "?"} avail`}
                </option>
              ))}
            </select>
          </div>
        )}

        {mode === "automatic" && (
          <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 inline-flex items-center gap-2">
            <Sparkle size={16} /> The system will pick the top-scoring available Executive.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Reason code {isReassign && "*"}</label>
            <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                    data-testid="reason-code"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm mt-5">
            <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                   data-testid="override-capacity" />
            <span>Override capacity limit</span>
          </label>
        </div>
        <div>
          <label className="text-xs text-slate-500">Reason (details) {isReassign && "*"}</label>
          <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="assign-reason"
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" />
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm border border-slate-200 rounded-md">Cancel</button>
          <button onClick={submit}
                  disabled={busy || (mode !== "automatic" && !employeeId) || (isReassign && !reason.trim())}
                  data-testid="submit-assign"
                  className="px-3 py-2 text-sm bg-slate-900 text-white rounded-md inline-flex items-center gap-1 disabled:opacity-50">
            <CheckCircle size={14} /> {isReassign ? "Reassign" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
